import { useState, useEffect } from 'react'

// Mantine
import { notifications } from '@mantine/notifications'
import { useDisclosure } from '@mantine/hooks'

// Next
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'

// UI
import ComponentHeaderRightSection from '@/components/components/ComponentHeaderRightSection'
import ComponentChildren from '@/components/components/ComponentChildren'
import ComponentParents from '@/components/components/ComponentParents'

import ComponentEditChildren from '@/components/components/ComponentEditChildren'
import ComponentFlow from '@/components/components/ComponentFlow'
import ComponentInventoryDetail from '@/components/components/ComponentInventoryDetail'
import ComponentInventoryChart from '@/components/components/ComponentInventoryChart'
import ComponentProductionOrders from '@/components/components/ComponentProductionOrders'
import ComponentParentsProductionOrders from '@/components/components/ComponentParentsProductionOrders'
import ComponentValue from '@/components/components/ComponentValue'
import ComponentEdit from '@/components/components/ComponentEdit'
import ComponentDelete from '@/components/components/ComponentDelete'
import ComponentPurchaseOrders from '@/components/components/ComponentPurchaseOrders'
import ComponentB2B from '@/components/components/ComponentSelling'
import ComponentFutureSelling from '@/components/components/ComponentFutureSelling'
import ComponentPhoto from '@/components/components/ComponentPhoto'
import ComponentWatchHeadWarranty from '@/components/components/ComponentWatchHeadWarranty'
import ComponentCoverage from '@/components/components/ComponentCoverage'

import Banner from '@/components/ui/Banner'

import Attachments from '@/components/ui/Attachments'
import ScrollableScreen from '@/components/ui/ScrollableScreen'

// Bus
import { useBus, useListener } from 'react-bus'

// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

// User
import useMe from '@/hooks/useMe'

export default function ComponentPage() {
    const navigate = useNavigate()
    const { component_id } = useParams()
    const [searchParams] = useSearchParams()
    const from = searchParams.get('from')

    // User
    const { isAdmin } = useMe()

    const [component, setComponent] = useState(null)
    const [variants, setVariants] = useState(null)

    const [componentFulfillmentOrders, setComponentFulfillmentOrders] =
        useState(null)

    const bus = useBus()

    // Load component
    const { requestGet, requestPatch, requestPost } = useAxiosApi()

    const loadComponent = async (scrollTop = false) => {
        const [component, fulfillQuantities, variants] = await Promise.all([
            requestGet(`/components/${component_id}`),
            requestGet(`/components/${component_id}/fulfill-quantities`),
            requestGet(`/components/${component_id}/shopify-variants`),
        ])

        setComponent(component.data)
        setComponentFulfillmentOrders(fulfillQuantities.data)
        setVariants(variants.data)

        if (scrollTop) {
            bus.emit('scrollTopContainer')
        }
    }

    useEffect(() => {
        if (component_id) {
            loadComponent(true)
        }
    }, [component_id])

    useListener('componentChanged', () => {
        loadComponent()
    })

    const onRefresh = async () => {
        await loadComponent()

        notifications.show({
            title: 'Données rafraichies',
            color: 'green',
        })
    }

    // Edit component
    const [editComponentOpened, editComponentHandlers] = useDisclosure(false)

    // Edit children
    const [editChildrenOpened, editChildrenHandlers] = useDisclosure(false)

    // Parents production orders
    const parentsProductionOrders =
        component?.parents.flatMap((parent) => parent.production_orders) ?? []

    const full_name = component
        ? component.component_category.name_fr + ' — ' + component.name
        : ''

    const sku = component
        ? component.component_category.sku_prefix +
          component.id.toString().padStart(4, '0')
        : ''

    // Breadcrumbs
    const breadcrumbs = [
        {
            label: 'Composants',
            href: from ? atob(from) : '/components',
        },
        {
            label: sku + ' - ' + full_name,
        },
    ]

    // Archive / unarchive
    const setArchived = async (is_archived) => {
        await requestPatch(`/components/${component_id}`, { is_archived })

        notifications.show({
            title: is_archived ? 'Composant archivé' : 'Composant désarchivé',
            color: 'green',
        })

        loadComponent()
    }

    const archiveComponent = () => setArchived(true)
    const unarchiveComponent = () => setArchived(false)

    // Delete component
    const deleteComponent = async () => {
        const { error } = await requestPost('/components/delete', {
            id: component_id,
        })

        if (error) {
            console.log(error)
        } else {
            notifications.show({
                title: 'Composant supprimé',
                color: 'green',
            })

            navigate(
                '/components?orderBy=created_at&orderByDirection=desc&location_id=1&columns=created_at'
            )
        }
    }

    return (
        <ScrollableScreen
            key={component_id}
            rightSection={
                <ComponentHeaderRightSection
                    onRefresh={onRefresh}
                    component={component}
                    editChildrenHandlers={editChildrenHandlers}
                    editComponentHandlers={editComponentHandlers}
                    archiveComponent={archiveComponent}
                    unarchiveComponent={unarchiveComponent}
                    deleteComponent={deleteComponent}
                />
            }
            breadcrumbs={breadcrumbs}
            commentableType="component"
            commentableId={component_id}
        >
            <div className="@container">
                {component && component.id === parseInt(component_id) && (
                    <div className="w-full max-w-[800px] @7xl:max-w-[1400px] mx-auto grid grid-cols-1 @7xl:grid-cols-[1fr_400px] items-start p-5 gap-5">
                        <div className="grid grid-cols-1 gap-5">
                            {component.is_archived && (
                                <Banner title="Composant archivé">
                                    <span>
                                        Le composant est archivé. Il ne concerne
                                        pas un produit actuellement vendu.
                                    </span>
                                </Banner>
                            )}

                            <ComponentInventoryDetail
                                component={component}
                                loadComponent={loadComponent}
                                fulfillmentOrders={componentFulfillmentOrders}
                            />

                            <ComponentInventoryChart
                                key={`inventory-chart-${component.id}`}
                                component={component}
                                fulfillmentOrders={componentFulfillmentOrders}
                                parentsProductionOrders={
                                    parentsProductionOrders
                                }
                            />

                            {component.children.length > 0 && (
                                <ComponentChildren
                                    component={component}
                                    editChildrenHandlers={editChildrenHandlers}
                                />
                            )}

                            {component.parents.length > 0 && (
                                <ComponentParents component={component} />
                            )}

                            <ComponentB2B
                                component={component}
                                loadComponent={loadComponent}
                                variants={variants}
                            />

                            {isAdmin && component.shopify_product_variants.length > 0 && (
                                <ComponentFutureSelling key={`future-selling-${component.id}`} componentId={component.id} />
                            )}

                            <ComponentCoverage
                                component={component}
                                loadComponent={loadComponent}
                                variants={variants}
                            />

                            <ComponentEditChildren
                                editChildrenOpened={editChildrenOpened}
                                editChildrenHandlers={editChildrenHandlers}
                                component={component}
                                onSaved={loadComponent}
                            />

                            <ComponentFlow component={component} />

                            {(component.children.length > 0 ||
                                component.shopify_product_variants.length > 0) && (
                                <ComponentProductionOrders
                                    component={component}
                                />
                            )}

                            {component.parents.length > 0 &&
                                parentsProductionOrders.length > 0 && (
                                    <ComponentParentsProductionOrders
                                        component={component}
                                        parentsProductionOrders={
                                            parentsProductionOrders
                                        }
                                    />
                                )}

                            <ComponentPurchaseOrders component={component} />

                            {component.category === 'watch_head' && (
                                <ComponentWatchHeadWarranty
                                    component={component}
                                    editComponentHandlers={
                                        editComponentHandlers
                                    }
                                />
                            )}

                            <ComponentEdit
                                component={component}
                                editComponentOpened={editComponentOpened}
                                editComponentHandlers={editComponentHandlers}
                                onSaved={loadComponent}
                            />

                            <ComponentDelete component={component} />
                        </div>

                        <div className="sticky top-5 flex flex-col gap-5">
                            {isAdmin && (
                                <ComponentValue
                                    component={component}
                                    openComponentEdit={() => {
                                        console.log('openComponentEdit')
                                        editComponentHandlers.open()
                                    }}
                                />
                            )}

                            <ComponentPhoto component={component} />

                            <Attachments
                                model_id={component.id}
                                model_type="component"
                                attachments={component.attachments}
                                refetchModelData={loadComponent}
                            />
                        </div>
                    </div>
                )}
            </div>
        </ScrollableScreen>
    )
}
